/* eslint-disable no-underscore-dangle */
import { formatBoolean, formatProDegree, useRouter } from '@application/utils';
import type { ProGetProQuery } from '@gql/graphql';
import { ProfessionalTypeEnum } from '@gql/graphql';
import type {
  DetailListProPracticalData,
  DetailListProPracticalItemType,
} from '@presentation/components';
import { useMemo } from 'react';
import { pickBy } from 'lodash/fp';

type Pro = NonNullable<ProGetProQuery['searchProfessionals']>['professionals'][number];

export const useFormatPracticalInfo = (pro: Pro | undefined, type?: ProfessionalTypeEnum) => {
  const { translate } = useRouter();
  const practicalInfo = useMemo((): DetailListProPracticalData | undefined => {
    if (!pro) return;
    const isUnclaimed = pro?.unclaimedProfile ?? false;

    const isVet = type === ProfessionalTypeEnum.VET;
    const isGroomer = type === ProfessionalTypeEnum.GROOMER;

    let items: DetailListProPracticalData['items'] = {
      employeeNumbers: pro?.employeesCount?.toString() || undefined,
      acceptedAnimals: !pro?.acceptedAnimals?.length
        ? undefined
        : pro?.acceptedAnimals
            .map((entry) => translate(`enum.animalType.${entry.toUpperCase()}`))
            .join(', '),
      acceptsNewClients: formatBoolean(translate, pro?.acceptsNewClients),
      ...(isVet && { shop: formatBoolean(translate, pro?.hasShop) }),
      ...(isGroomer && { shopGroomer: formatBoolean(translate, pro?.hasShop) }),
      onlineAppointment: formatBoolean(
        translate,
        pro?.onlineBookingEnabled || pro?.acceptsOnlineAppointment,
      ),
      paymentMethods: !pro?.paymentMethods?.length
        ? undefined
        : pro?.paymentMethods
            .map((entry) => translate(`glossary.paymentMethodsList.${entry}`))
            .join(', '),
      parking: formatBoolean(translate, pro?.parking),
      visitAtHome: formatBoolean(translate, pro?.visitAtHome),
      ...(isGroomer && { selfDogWash: formatBoolean(translate, pro?.selfDogWash) }),
      ...(isGroomer && { formations: formatProDegree(pro?.degrees) }),
      ...(isVet && { graduated: formatProDegree(pro?.degrees) }),
    };
    if (isUnclaimed) {
      const unclaimedShownItems: DetailListProPracticalItemType[] = [
        'acceptedAnimals',
        'onlineAppointment',
        'acceptsNewClients',
        'paymentMethods',
      ];
      items = pickBy(
        (value, key) => unclaimedShownItems.includes(key as keyof typeof items),
        items,
      );
    }

    return { items };
  }, [pro, translate, type]);
  return {
    practicalInfo,
  };
};
